import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { workspacesApi, wordpressApi } from '../lib/api';
import { Spinner, Alert, BackLink } from '@rankhelm/ui';
import type { Workspace, WordPressTestResult, SeoPlugin } from '@rankhelm/shared';

export function WorkspaceSettingsPage() {
  const { workspaceId } = useParams();
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<WordPressTestResult | null>(null);

  const [form, setForm] = useState({
    name: '',
    website_url: '',
    publish_target_type: 'wordpress',
    seo_plugin: '',
    nap_enabled: false,
    wp_url: '',
    wp_username: '',
    wp_app_password: '',
    nap_business_name: '',
    nap_address: '',
    nap_city: '',
    nap_state: '',
    nap_zip: '',
    nap_phone: '',
  });

  useEffect(() => {
    if (!workspaceId) return;
    async function load() {
      try {
        const res = await workspacesApi.get(workspaceId!);
        const w = res.data;
        setWorkspace(w);

        let wpConfig: Record<string, string> = {};
        try { wpConfig = JSON.parse(w.publish_config_json || '{}'); } catch { /* invalid JSON, use defaults */ }
        let napData: Record<string, string> = {};
        try { napData = JSON.parse(w.nap_data_json || '{}'); } catch { /* invalid JSON, use defaults */ }

        setForm({
          name: w.name,
          website_url: w.website_url || '',
          publish_target_type: w.publish_target_type,
          seo_plugin: w.seo_plugin || '',
          nap_enabled: w.nap_enabled,
          wp_url: wpConfig.url || '',
          wp_username: wpConfig.username || '',
          wp_app_password: wpConfig.app_password_enc || '',
          nap_business_name: napData.business_name || '',
          nap_address: napData.address || '',
          nap_city: napData.city || '',
          nap_state: napData.state || '',
          nap_zip: napData.zip || '',
          nap_phone: napData.phone || '',
        });
      } catch (e) {
        console.error('Failed to load workspace:', e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [workspaceId]);

  const update = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.type === 'checkbox' ? (e.target as HTMLInputElement).checked : e.target.value;
    setForm({ ...form, [field]: value });
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setMessage('');

    try {
      const napDataJson = form.nap_enabled ? JSON.stringify({
        business_name: form.nap_business_name,
        address: form.nap_address,
        city: form.nap_city,
        state: form.nap_state,
        zip: form.nap_zip,
        phone: form.nap_phone,
      }) : null;

      await workspacesApi.update(workspaceId!, {
        name: form.name,
        website_url: form.website_url || null,
        publish_target_type: form.publish_target_type as 'wordpress',
        publish_config: {
          url: form.wp_url,
          app_password_enc: form.wp_app_password,
          username: form.wp_username,
        },
        seo_plugin: (form.seo_plugin || null) as SeoPlugin | null,
        nap_enabled: form.nap_enabled,
        nap_data_json: napDataJson,
      });
      setMessage('Settings saved successfully.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!form.wp_url) {
      setTestResult({ connected: false, error: 'Enter a WordPress URL first.' });
      return;
    }
    setTesting(true);
    setTestResult(null);
    try {
      const res = await wordpressApi.testConnection({
        url: form.wp_url,
        app_password: form.wp_app_password,
        username: form.wp_username,
      });
      setTestResult(res.data);
    } catch (err) {
      setTestResult({ connected: false, can_publish: false, error: err instanceof Error ? err.message : 'Connection failed' } as WordPressTestResult);
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <Spinner message="Loading workspace settings..." fullPage />;
  if (!workspace) return <div className="text-danger">Workspace not found</div>;

  return (
    <div>
      <BackLink to={`/workspaces/${workspaceId}`} label="Back to Workspace" />
      <h1 className="text-2xl font-bold text-white mb-6">Workspace Settings</h1>

      {error && <Alert variant="error">{error}</Alert>}
      {message && <Alert variant="success">{message}</Alert>}

      <form onSubmit={handleSave} className="space-y-8 max-w-2xl">
        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
          <h2 className="text-lg font-semibold text-white">General</h2>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Workspace Name</label>
            <input type="text" value={form.name} onChange={update('name')} required className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Website URL</label>
            <input type="url" value={form.website_url} onChange={update('website_url')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" placeholder="https://example.com" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Publish Target</label>
            <select value={form.publish_target_type} onChange={update('publish_target_type')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500">
              <option value="wordpress">WordPress</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">SEO Plugin</label>
            <select value={form.seo_plugin} onChange={update('seo_plugin')} className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-helm-500">
              <option value="">None / Other</option>
              <option value="yoast">Yoast SEO</option>
              <option value="rankmath">RankMath</option>
              <option value="aioseo">All in One SEO (AIOSEO)</option>
            </select>
            <p className="text-xs text-slate-500 mt-1">Changing this affects future publishes only.</p>
          </div>
        </div>

        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-white">WordPress Connection</h2>
            <button type="button" onClick={handleTestConnection} disabled={testing} className="px-4 py-2 bg-navy-800 hover:bg-navy-700 text-slate-300 text-sm rounded-lg border border-navy-600 transition-colors disabled:opacity-50">
              {testing ? 'Testing...' : 'Test Connection'}
            </button>
          </div>
          {testResult && (
            <div className={`text-sm rounded-lg px-4 py-3 ${testResult.connected && testResult.can_publish ? 'bg-green-500/10 border border-green-500/30 text-green-400' : 'bg-orange-500/10 border border-orange-500/30 text-orange-400'}`}>
              {testResult.connected && testResult.can_publish ? (
                <span>Connected to <strong>{testResult.site_name}</strong> (WP {testResult.wp_version}). Publishing permissions confirmed.</span>
              ) : testResult.connected ? (
                <span>Site found ({testResult.site_name}) but: {testResult.error}</span>
              ) : (
                <span>{testResult.error || 'Connection failed.'}</span>
              )}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">WordPress Site URL</label>
            <input type="url" value={form.wp_url} onChange={update('wp_url')} placeholder="https://yoursite.com" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">WordPress Username</label>
            <input type="text" value={form.wp_username} onChange={update('wp_username')} placeholder="admin" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1.5">Application Password</label>
            <input type="password" value={form.wp_app_password} onChange={update('wp_app_password')} placeholder="xxxx xxxx xxxx xxxx xxxx xxxx" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
            <p className="text-xs text-slate-500 mt-1">Generate in WordPress: Users → Profile → Application Passwords. Requires admin or editor role.</p>
          </div>
        </div>

        <div className="bg-navy-900 border border-navy-700 rounded-xl p-6 space-y-5">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={form.nap_enabled} onChange={update('nap_enabled')} className="w-4 h-4 rounded border-navy-600 text-helm-500 focus:ring-helm-500 bg-navy-800" />
            <div>
              <span className="text-lg font-semibold text-white">NAP Schema (Local Business)</span>
              <p className="text-xs text-slate-400">Injects LocalBusiness schema (Name, Address, Phone) into every published article.</p>
            </div>
          </label>
          {form.nap_enabled && (
            <div className="space-y-4 pt-2 border-t border-navy-700">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Business Name</label>
                <input type="text" value={form.nap_business_name} onChange={update('nap_business_name')} placeholder="Acme Roofing LLC" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Street Address</label>
                <input type="text" value={form.nap_address} onChange={update('nap_address')} placeholder="123 Main Street" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">City</label>
                  <input type="text" value={form.nap_city} onChange={update('nap_city')} placeholder="Springfield" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">State</label>
                  <input type="text" value={form.nap_state} onChange={update('nap_state')} maxLength={2} placeholder="IL" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-1.5">ZIP</label>
                  <input type="text" value={form.nap_zip} onChange={update('nap_zip')} placeholder="62701" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1.5">Phone Number</label>
                <input type="tel" value={form.nap_phone} onChange={update('nap_phone')} placeholder="(555) 123-4567" className="w-full px-4 py-2.5 bg-navy-800 border border-navy-600 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-helm-500" />
              </div>
            </div>
          )}
        </div>

        <button type="submit" disabled={saving} className="px-8 py-3 bg-helm-500 hover:bg-helm-400 text-white font-semibold rounded-lg transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </form>
    </div>
  );
}
